import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { SaveIcon, Link as LinkIcon, Award } from 'lucide-react';
import { CloudArrowUpIcon } from '@heroicons/react/24/outline';

const sanitizeAndFormatHtml = (content) => {
    if (!content || typeof content !== 'string') return '';
    let html = content;
    html = html.replace(/<a\s+[^>]*?>/gi, (tag) => {
        const attrMatch = tag.match(/(?:["']?(?:link|href)\s*=\s*\\?["']?\s*)([^"'\\>]+?)(?:\s*\\?["']?\s*)(?:>|\s|$)/i);
        if (!attrMatch) return tag;
        const rawUrl = attrMatch[1].trim().replace(/^["']|["']$/g, '');
        const targetMatch = tag.match(/target\s*=\s*["']?([^"'>\s]+)["']?/i);
        const targetAttr = targetMatch ? ` target="${targetMatch[1]}"` : '';
        return `<a href="${rawUrl}"${targetAttr}>`;
    });
    if (!/<(?:br|p|div)\b/i.test(html)) {
        html = html.replace(/\r?\n/g, '<br />');
    }
    return html;
};

const InvestorTrustSettingsPage = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [settings, setSettings] = useState({
        section_tag: 'Trust & Integrity',
        section_heading: 'Responsible Governance, Strong Ethics',
        lead_text: '',
        image_url: '',
        credit_rating_tag: 'Rating & Accreditation',
        credit_rating_title: 'Credit Rating Certificates',
        credit_rating_text: 'View Aham Housing Finance\'s latest <a href="https://ahamhfc.com/aham-housing-finance-credit-rating-certificate">credit rating certificates</a> and related rating information.'
    });
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);

    // Link helper state
    const [showLinkModal, setShowLinkModal] = useState(false);
    const [linkText, setLinkText] = useState('');
    const [linkUrl, setLinkUrl] = useState('');

    const handleInsertLink = () => {
        if (!linkUrl.trim()) {
            toast.error('Please enter a valid URL (e.g. https://ahamhfc.com/aham-housing-finance-credit-rating-certificate)');
            return;
        }
        const label = linkText.trim() || 'credit rating certificates';
        const cleanUrl = linkUrl.trim();
        const tag = `<a href="${cleanUrl}">${label}</a>`;
        const currentValue = settings.credit_rating_text || '';
        const updatedValue = currentValue ? `${currentValue} ${tag}` : tag;
        setSettings(prev => ({ ...prev, credit_rating_text: updatedValue }));
        toast.success(`Inserted link: ${label}`);
        setShowLinkModal(false);
        setLinkText('');
        setLinkUrl('');
    };

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            setLoading(true);
            const response = await axios.get('/api/admin/investor-trust/settings');
            if (response.data.success && response.data.data) {
                const data = response.data.data;
                setSettings(prev => ({
                    ...prev,
                    ...data,
                    credit_rating_tag: data.credit_rating_tag || 'Rating & Accreditation',
                    credit_rating_title: data.credit_rating_title || 'Credit Rating Certificates',
                    credit_rating_text: data.credit_rating_text || 'View Aham Housing Finance\'s latest <a href="https://ahamhfc.com/aham-housing-finance-credit-rating-certificate">credit rating certificates</a> and related rating information.'
                }));
                if (data.image_url) {
                    setImagePreview(`http://localhost:8001${data.image_url}`);
                }
            }
        } catch (error) {
            console.error('Error fetching settings:', error);
            toast.error('Failed to load settings');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setSettings(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setImageFile(file);
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setSaving(true);

            const formData = new FormData();
            formData.append('section_tag', settings.section_tag || '');
            formData.append('section_heading', settings.section_heading || '');
            formData.append('lead_text', settings.lead_text || '');
            formData.append('credit_rating_tag', settings.credit_rating_tag || '');
            formData.append('credit_rating_title', settings.credit_rating_title || '');
            formData.append('credit_rating_text', settings.credit_rating_text || '');

            if (!imageFile) {
                formData.append('image_url', settings.image_url || '');
            }

            if (imageFile) {
                formData.append('image', imageFile);
            }

            await axios.put('/api/admin/investor-trust/settings', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            toast.success('Settings updated successfully');
            setImageFile(null);
            fetchSettings();
        } catch (error) {
            console.error('Error updating settings:', error);
            toast.error('Failed to update settings');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-bold text-slate-800">Trust & Integrity - Settings</h1>
                <p className="text-slate-500 mt-1">Manage the "Trust & Integrity" and "Credit Rating Certificates" sections</p>
            </div>

            <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-6 shadow-sm space-y-6">
                <div className="border-b border-slate-100 pb-4 mb-4">
                    <h2 className="text-lg font-bold text-slate-800">Governance Section (Responsible Governance, Strong Ethics)</h2>
                </div>

                <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Section Tag</label>
                    <input
                        type="text"
                        name="section_tag"
                        value={settings.section_tag}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="Trust & Integrity"
                    />
                </div>

                <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Section Heading</label>
                    <input
                        type="text"
                        name="section_heading"
                        value={settings.section_heading}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="Responsible Governance, Strong Ethics"
                    />
                </div>

                <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Lead Text</label>
                    <textarea
                        name="lead_text"
                        value={settings.lead_text}
                        onChange={handleChange}
                        rows="3"
                        className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="Our governance approach ensures..."
                    />
                </div>

                <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Governance Image</label>
                    <p className="text-xs text-slate-500 mb-3">📐 Recommended: 800 x 600 pixels | Formats: WebP, PNG, JPEG</p>
                    <div className="flex gap-4">
                        <div className="flex-1">
                            <label className="block border-2 border-dashed border-slate-300 rounded-xl p-6 text-center cursor-pointer hover:border-primary-500 transition-colors">
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageChange}
                                    className="hidden"
                                />
                                <CloudArrowUpIcon className="h-12 w-12 text-slate-400 mx-auto mb-2" />
                                <p className="text-sm font-medium text-slate-600">
                                    {imagePreview ? 'Change Image' : 'Upload Image'}
                                </p>
                                <p className="text-xs text-slate-400 mt-1">Max 10MB</p>
                            </label>
                        </div>
                        {imagePreview && (
                            <div className="w-48 h-32 rounded-xl overflow-hidden border-2 border-slate-200">
                                <img
                                    src={imagePreview}
                                    alt="Preview"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                        )}
                    </div>
                </div>

                {/* Credit Rating Certificates Section */}
                <div className="border-t border-slate-200 pt-8 mt-8">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h2 className="text-lg font-bold text-slate-800">Credit Rating Certificates Section</h2>
                            <p className="text-xs text-slate-500">Appears immediately after Responsible Governance, Strong Ethics and before the footer.</p>
                        </div>
                        <button
                            type="button"
                            onClick={() => {
                                setShowLinkModal(!showLinkModal);
                                setLinkText('');
                                setLinkUrl('');
                            }}
                            className="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100 px-3 py-1.5 rounded-lg transition-colors border border-indigo-200"
                        >
                            <LinkIcon size={14} />
                            <span>{showLinkModal ? 'Cancel Link' : '+ Insert Link'}</span>
                        </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">Section Tag</label>
                            <input
                                type="text"
                                name="credit_rating_tag"
                                value={settings.credit_rating_tag || ''}
                                onChange={handleChange}
                                className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                                placeholder="Rating & Accreditation"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">Section Heading</label>
                            <input
                                type="text"
                                name="credit_rating_title"
                                value={settings.credit_rating_title || ''}
                                onChange={handleChange}
                                className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                                placeholder="Credit Rating Certificates"
                            />
                        </div>
                    </div>

                    {showLinkModal && (
                        <div className="bg-indigo-50/80 border border-indigo-200 rounded-xl p-3 mb-3 space-y-2">
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-bold text-indigo-900">Insert Link into Credit Rating Content</span>
                                <span className="text-[11px] text-indigo-600">e.g. /aham-housing-finance-credit-rating-certificate or https://...</span>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                <div>
                                    <label className="block text-[11px] font-semibold text-slate-600 mb-1">Link Text</label>
                                    <input
                                        type="text"
                                        value={linkText}
                                        onChange={e => setLinkText(e.target.value)}
                                        placeholder="e.g. credit rating certificates"
                                        className="w-full bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-xs focus:ring-1 focus:ring-indigo-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-[11px] font-semibold text-slate-600 mb-1">Target URL</label>
                                    <input
                                        type="text"
                                        value={linkUrl}
                                        onChange={e => setLinkUrl(e.target.value)}
                                        placeholder="e.g. https://ahamhfc.com/aham-housing-finance-credit-rating-certificate"
                                        className="w-full bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-xs focus:ring-1 focus:ring-indigo-500"
                                    />
                                </div>
                            </div>
                            <div className="flex justify-end gap-2 pt-1">
                                <button
                                    type="button"
                                    onClick={() => setShowLinkModal(false)}
                                    className="px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-200 rounded-lg"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleInsertLink}
                                    className="px-3 py-1 text-xs font-bold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 shadow-sm"
                                >
                                    Insert &lt;a&gt; Tag
                                </button>
                            </div>
                        </div>
                    )}

                    <div className="mb-4">
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Card Content (Inter-Link Supported)</label>
                        <textarea
                            name="credit_rating_text"
                            value={settings.credit_rating_text || ''}
                            onChange={handleChange}
                            rows="3"
                            className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm font-normal"
                            placeholder="View Aham Housing Finance's latest <a href=&quot;https://ahamhfc.com/aham-housing-finance-credit-rating-certificate&quot;>credit rating certificates</a> and related rating information."
                        />
                        <p className="mt-1 text-[11px] text-slate-500">
                            Tip: HTML anchor tags and line breaks are fully supported.
                        </p>
                    </div>

                    {/* Live Preview of Credit Rating Box */}
                    {settings.credit_rating_text && (
                        <div className="bg-[#1e1b4b] p-5 rounded-2xl border border-yellow-500/30 text-white space-y-3 shadow-md">
                            <div className="flex items-center justify-between border-b border-white/15 pb-2.5">
                                <div className="flex items-center gap-2">
                                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse"></span>
                                    <span className="text-xs font-bold uppercase tracking-wider text-yellow-400">CREDIT RATING BOX LIVE PREVIEW</span>
                                </div>
                                <span className="text-xs text-gray-300">Live preview with clickable links</span>
                            </div>
                            <div className="flex items-center gap-4 pt-1">
                                <Award className="w-10 h-10 text-yellow-400 flex-shrink-0" />
                                <div
                                    className="text-gray-200 text-sm leading-relaxed [&_a]:text-yellow-400 [&_a]:underline hover:[&_a]:text-yellow-300 [&_a]:font-semibold [&_a]:transition-colors"
                                    dangerouslySetInnerHTML={{ __html: sanitizeAndFormatHtml(settings.credit_rating_text) }}
                                />
                            </div>
                        </div>
                    )}
                </div>

                <div className="flex justify-end pt-4">
                    <button
                        type="submit"
                        disabled={saving}
                        className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-8 py-3 rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <SaveIcon size={20} />
                        {saving ? 'Saving...' : 'Save Settings'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default InvestorTrustSettingsPage;
